// Kinio 公版 webapp 的 brand 解析(common/08 §2.1.5;epic APP#58)。
// 同一份 build 依「進入的 domain」決定生態系:本機 host 用內建 config;
// 部署 host 啟動時打公開的 /v3/brand/bootstrap(CORS simple GET,無自訂
// header——不能反過來要求 client 憑證),動態注入 client_id/app_key、
// 品牌(§2.1.4 解析:名稱空→Kinio、icon 空→內建)與 capabilities。
// 未綁定的 domain → not provisioned 頁,絕不 fallback 官方生態(防止
// 未 onboard domain 靜默落入 fam-platform)。
import { config } from '../config';
import { DEFAULT_PASSWORD_POLICY, normalizePasswordPolicy } from './passwordPolicy';
import type { PasswordPolicy } from './passwordPolicy';

export const DEFAULT_BRAND_NAME = 'Kinio';

/** 本機開發 host：使用 build 內建 config，不打 bootstrap。 */
const BUILT_IN_HOSTS = new Set(['localhost', '127.0.0.1']);

// v3-webapp-dev is being cut over from the platform client to Living Style.
// Before its client row is provisioned, an explicit bootstrap 404 keeps the
// current built-in client alive. Once provisioned, the same build switches to
// the returned Living Style client without another deploy.
const PROVISIONING_FALLBACK_HOSTS = new Set(['v3-webapp-dev.ls-dg.net']);

export interface BrandInfo {
  /** 顯示名(§2.1.4:display_name 空 → Kinio)。built-in host 不改動既有 UI 字串。 */
  name: string;
  /** icon data URL;null=用既有 favicon/內建。 */
  icon: string | null;
  /** 生態 capabilities;null=官方(不 gate)。UI gating 僅 UX,伺服端 fail-closed。 */
  caps: string[] | null;
  /** domain 是否已綁定 brand(false → 顯示未啟用頁)。 */
  provisioned: boolean;
  /** 是否已由 bootstrap 解析為 brand host。 */
  isBrandHost: boolean;
  /** 有效密碼政策(APP#59/restful#76):brand host=bootstrap 解析後有效政策;built-in=平台預設。 */
  passwordPolicy: PasswordPolicy;
}

export const brand: BrandInfo = {
  name: DEFAULT_BRAND_NAME,
  icon: null,
  caps: null,
  provisioned: true,
  isBrandHost: false,
  passwordPolicy: DEFAULT_PASSWORD_POLICY,
};

interface BootstrapPayload {
  client_id: string;
  app_key: string;
  display_name?: string;
  icon_data?: string;
  capabilities?: string[];
  password_policy?: unknown;
}

/**
 * 啟動時解析 brand;必須在第一個需要 client 憑證的請求(login/refresh)前完成
 * —— main.tsx 於 render 前 await。失敗(網路/5xx)保守視為未啟用,不用官方憑證。
 */
export async function resolveBrand(hostname: string = window.location.hostname): Promise<BrandInfo> {
  const host = hostname.toLowerCase();
  if (BUILT_IN_HOSTS.has(host)) return brand;
  // ls-loop（MW）：單一產品、沒有多品牌網域綁定 → 建置時 VITE_BRAND_BOOTSTRAP=off 就把任何網域當內建（用 config 的 client）
  if (import.meta.env.VITE_BRAND_BOOTSTRAP === 'off') return brand;
  brand.isBrandHost = true;
  try {
    // simple GET:無自訂 header(避 preflight;bootstrap 是取憑證的端點)。
    const res = await fetch(`${config.apiBase}/v3/brand/bootstrap?host=${encodeURIComponent(host)}`);
    if (!res.ok) {
      if (res.status === 404 && PROVISIONING_FALLBACK_HOSTS.has(host)) {
        brand.isBrandHost = false;
        return brand;
      }
      brand.provisioned = false;
      return brand;
    }
    const payload = (await res.json())?.data as BootstrapPayload | undefined;
    if (!payload?.client_id || !payload.app_key) {
      brand.provisioned = false;
      return brand;
    }
    // 動態注入 client 憑證:此後所有 REST(X-Client-Id/Key)與 per-client
    // refresh cookie(refresh_token__{client_id})都走該生態。
    config.clientId = payload.client_id;
    config.clientKey = payload.app_key;
    brand.name = payload.display_name?.trim() ? payload.display_name : DEFAULT_BRAND_NAME;
    brand.icon = payload.icon_data?.trim() ? payload.icon_data : null;
    brand.caps = Array.isArray(payload.capabilities) ? payload.capabilities : [];
    // bootstrap 回的是「解析後有效政策」;缺欄/畸形時 normalize 落回平台預設。
    brand.passwordPolicy = normalizePasswordPolicy(payload.password_policy);
    applyBrandChrome();
    return brand;
  } catch {
    brand.provisioned = false;
    return brand;
  }
}

/** 生態功能 gate(null=官方全開)。UI 層 UX;真正安全邊界=伺服端 scope。 */
/** 品牌顯示名(nav/login):brand host=bootstrap 名稱(§2.1.4 空→Kinio);built-in 維持 LS Cloud。 */
export function brandDisplayName(): string {
  // 內建名稱（ls-loop MW：VITE_APP_NAME=KindTend 親疼；沒設照主線 LS Cloud）
  return brand.isBrandHost ? brand.name : (import.meta.env.VITE_APP_NAME || 'LS Cloud');
}

/** 品牌 logo src:brand host=icon_data(空→內建 Kinio 圖);built-in=既有 favicon。 */
export function brandLogoSrc(): string {
  if (brand.isBrandHost) return brand.icon ?? '/kinio-icon.png';
  return '/favicon.png';
}

export function hasBrandCap(cap: string): boolean {
  return brand.caps == null || brand.caps.includes(cap);
}

function applyBrandChrome() {
  document.title = brand.name;
  if (brand.icon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = brand.icon;
  }
}
